import * as XLSX from 'xlsx';
import { adminService } from './adminService';

// Helper functions
const parseBudget = (budgetValue) => {
  if (!budgetValue) return 0;
  
  if (typeof budgetValue === 'number') {
    return budgetValue;
  }
  
  const budgetStr = budgetValue.toString().replace(/[^\d.]/g, '');
  return parseFloat(budgetStr) || 0;
};

const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim().replace(/"/g, ''));
      current = '';
    } else {
      current += char;
    }
  }
  
  values.push(current.trim().replace(/"/g, ''));
  return values;
};

export const importService = {
  // Parse Excel file and extract leads data
  parseExcelFile: async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          // Validate and transform data
          const leads = jsonData.map((row, index) => {
            const lead = {
              fullName: row['Full Name'] || row['Name'] || '',
              email: row['Email'] || '',
              mobileNo: row['Phone Number'] || row['Phone'] || row['Mobile'] || '',
              propertyType: row['Property Type'] || '',
              budget: parseBudget(row['Budget']),
              location: row['Location'] || '',
              source: row['Source'] || 'Import',
              status: 'New', // Default status for imported leads
              notes: row['Notes'] || `Imported on ${new Date().toLocaleDateString()}`,
              // These will be set during import
              assignedAgentId: '',
              assignedAgentEmail: '',
              assignedAgentName: '',
              importDate: new Date(),
              isImported: true
            };

            // Validate required fields
            if (!lead.fullName.trim()) {
              throw new Error(`Row ${index + 2}: Full Name is required`);
            }
            if (!lead.email.trim() && !lead.mobileNo.trim()) {
              throw new Error(`Row ${index + 2}: Either Email or Phone Number is required`);
            }

            return lead;
          });

          resolve(leads);
        } catch (error) {
          reject(new Error('Failed to parse Excel file: ' + error.message));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsArrayBuffer(file);
    });
  },

  // Parse CSV file and extract leads data
  parseCSVFile: async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const csvText = e.target.result;
          const lines = csvText.split('\n').filter(line => line.trim());
          const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
          
          const leads = [];
          
          for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            const row = {};
            
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });

            const lead = {
              fullName: row['Full Name'] || row['Name'] || '',
              email: row['Email'] || '',
              mobileNo: row['Phone Number'] || row['Phone'] || row['Mobile'] || '',
              propertyType: row['Property Type'] || '',
              budget: parseBudget(row['Budget']),
              location: row['Location'] || '',
              source: row['Source'] || 'Import',
              status: 'New',
              notes: row['Notes'] || `Imported on ${new Date().toLocaleDateString()}`,
              assignedAgentId: '',
              assignedAgentEmail: '',
              assignedAgentName: '',
              importDate: new Date(),
              isImported: true
            };

            // Validate required fields
            if (!lead.fullName.trim()) {
              throw new Error(`Row ${i + 1}: Full Name is required`);
            }
            if (!lead.email.trim() && !lead.mobileNo.trim()) {
              throw new Error(`Row ${i + 1}: Either Email or Phone Number is required`);
            }

            leads.push(lead);
          }

          resolve(leads);
        } catch (error) {
          reject(new Error('Failed to parse CSV file: ' + error.message));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  },

  // Import leads to Firestore
  importLeads: async (leads, assignToAgent = null) => {
    try {
      const results = {
        total: leads.length,
        success: 0,
        failed: 0,
        errors: []
      };

      // Import leads in batches to avoid timeout
      const batchSize = 10;
      for (let i = 0; i < leads.length; i += batchSize) {
        const batch = leads.slice(i, i + batchSize);
        
        const batchResults = await Promise.allSettled(
          batch.map(lead => adminService.importLead(lead, assignToAgent))
        );

        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            results.success++;
          } else {
            results.failed++;
            results.errors.push(`Lead ${i + index + 1}: ${result.reason.message}`);
          }
        });

        // Small delay between batches to avoid overwhelming the server
        if (i + batchSize < leads.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      return results;
    } catch (error) {
      throw new Error('Import failed: ' + error.message);
    }
  },

  // Download import template
  downloadTemplate: (format = 'excel') => {
    const templateData = [
      {
        'Full Name': 'John Doe',
        'Email': 'john.doe@example.com',
        'Phone Number': '+1234567890',
        'Property Type': 'Residential',
        'Budget': '5000000',
        'Location': 'New York',
        'Source': 'Website',
        'Notes': 'Interested in 3-bedroom apartment'
      }
    ];

    if (format === 'excel') {
      const worksheet = XLSX.utils.json_to_sheet(templateData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
      
      // Set column widths
      const colWidths = [
        { wch: 20 }, { wch: 25 }, { wch: 15 }, 
        { wch: 15 }, { wch: 15 }, { wch: 15 }, 
        { wch: 12 }, { wch: 30 }
      ];
      worksheet['!cols'] = colWidths;

      XLSX.writeFile(workbook, 'leads_import_template.xlsx');
    } else {
      const headers = ['Full Name', 'Email', 'Phone Number', 'Property Type', 'Budget', 'Location', 'Source', 'Notes'];
      const csvContent = [
        headers.join(','),
        ...templateData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'leads_import_template.csv';
      link.click();
      URL.revokeObjectURL(url);
    }
  }
};